<template>
  <div class="bcb-campaign-send-notification">
    <div class="row">
      <div class="col-md-12">
        <BasicRadio
          label="Chế độ soạn email"
          name="composeMode"
          :options="composeModeOptions"
          :value.sync="composeMode"
          :inline="true"
        />
      </div>
    </div>

    <!-- Mode 0: dùng template có sẵn -->
    <div v-if="composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE" :key="`template-${composeMode}`" class="row">
      <div class="col-md-6">
        <BasicSelect
          label="Chọn template"
          placeholder="Chọn template tin nhắn"
          name="templateMessageId"
          :options="templateOptions"
          track-by="id"
          value-label="name"
          :change-value-by-object="true"
          :value.sync="selectedTemplate"
        />
      </div>
      <div class="col-md-12" v-if="selectedTemplatePreview">
        <label class="font-weight-bold">Xem trước nội dung</label>
        <div class="template-preview border rounded p-4" v-html="selectedTemplatePreview"></div>
      </div>
    </div>

    <!-- Mode 1: soạn tuỳ chỉnh -->
    <div v-else :key="`custom-${composeMode}`">
      <div class="row">
        <div class="col-md-12">
          <basic-input
            label="Tiêu đề (*)"
            placeholder="Nhập tiêu đề email"
            name="customSubject"
            :value.sync="customSubject"
            required
          />
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <BasicTextEditors
            label="Nội dung"
            placeholder="Soạn nội dung email..."
            name="customBody"
            :value="customBody"
            @input="customBody = $event"
          />
          <small class="text-muted d-block mt-1">
            Có thể dùng các tag để cá nhân hoá khi gửi riêng từng người, ví dụ:
            &#123;&#123;FullName&#125;&#125;, &#123;&#123;PartnerName&#125;&#125;, &#123;&#123;ScheduleSlot&#125;&#125;...
          </small>
          <small class="text-muted d-block mt-1">
            Email tự soạn sẽ được gửi ngay lập tức, không hỗ trợ lên lịch gửi.
          </small>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <label class="font-weight-bold d-block">Đính kèm file</label>
          <button type="button" class="btn btn-outline-secondary btn-sm mb-2" @click="$refs.fileInput.click()">
            <i class="fas fa-paperclip mr-2"></i>Đính kèm file
          </button>
          <input ref="fileInput" v-show="false" type="file" multiple @change="onFilesSelected" />
          <ul v-if="attachments.length" class="attachment-list list-unstyled">
            <li v-for="(file, idx) in attachments" :key="idx" class="d-flex align-items-center mb-1">
              <span class="mr-2">{{ file.name }} ({{ formatSize(file.size) }})</span>
              <i class="fas fa-times text-danger cursor-pointer" @click="removeAttachment(idx)"></i>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <hr />

    <!-- Chọn người nhận -->
    <div class="row">
      <div class="col-md-12">
        <BasicRadio
          label="Chọn người nhận theo"
          name="recipientMode"
          :options="recipientModeOptions"
          :value.sync="recipientMode"
          :inline="true"
        />
      </div>
    </div>

    <!-- Gửi theo trạng thái: chỉ cần chọn trạng thái, khi bấm "Gửi thông báo" sẽ tự gửi cho toàn bộ KH khớp -->
    <div v-if="recipientMode === RECIPIENT_MODE.STATUS" class="row mb-3">
      <div class="col-md-8">
        <BasicSelect
          label="Chọn trạng thái"
          placeholder="Chọn 1 hoặc nhiều trạng thái"
          name="statusSelections"
          :options="statusFilterOptions"
          track-by="value"
          value-label="text"
          :multiple="true"
          :value.sync="statusSelections"
        />
        <small class="text-muted d-block mt-1">
          Khi bấm "Gửi thông báo", hệ thống sẽ tự gửi cho toàn bộ khách hàng thuộc (các) trạng thái đã chọn.
        </small>
      </div>
    </div>

    <!-- Gửi cá nhân: tìm theo tên/SĐT rồi chọn từng KH cụ thể -->
    <div v-if="recipientMode === RECIPIENT_MODE.INDIVIDUAL" class="mb-3">
      <label class="font-weight-bold d-block mb-2">Tìm kiếm khách hàng</label>
      <div class="d-flex align-items-start">
        <basic-input
          placeholder="Nhập tên hoặc số điện thoại..."
          name="searchKeyword"
          :value.sync="searchKeyword"
          class="flex-shrink-0 mr-2"
          style="max-width: 320px"
          @keyup.enter.native="searchCustomers"
        />
        <LoadingButton class="btn btn-outline-success flex-shrink-0" style="min-width: 140px" :handler="searchCustomers">
          <i class="fas fa-search mr-2"></i>Tìm kiếm
        </LoadingButton>
      </div>

      <b-overlay v-if="searched" :show="searchLoading" class="mt-3">
        <template-table
          :column="recipientColumns"
          :data="searchResults"
          :select-action="true"
          :btn-inactive-selected="false"
          :search-action="false"
          :table-action="false"
          :paging-action="false"
          @select="onSelectSearchResults"
        >
          <template #body="{ item }">
            <td>{{ item.fullName || '—' }}</td>
            <td>{{ item.email || '—' }}</td>
            <td>{{ item.phone || '—' }}</td>
            <td>
              <span class="status-badge" :style="getCustomerStatusStyle(item.status)">
                {{ getCustomerStatusLabel(item.status) }}
              </span>
            </td>
          </template>
        </template-table>
      </b-overlay>
    </div>

    <!-- Danh sách đã chọn (chỉ áp dụng cho chế độ gửi cá nhân) -->
    <div v-if="recipientMode === RECIPIENT_MODE.INDIVIDUAL" class="row mt-2">
      <div class="col-md-12">
        <label class="font-weight-bold d-block">Đã chọn {{ selectedRecipientIds.length }} khách hàng</label>
        <ul v-if="selectedRecipientsList.length" class="selected-recipient-list list-unstyled">
          <li v-for="item in selectedRecipientsList" :key="item.id" class="d-flex align-items-center mb-1">
            <span class="mr-2">{{ item.fullName || '—' }} ({{ item.phone || item.email || '—' }})</span>
            <i class="fas fa-times text-danger cursor-pointer" @click="removeRecipient(item.id)"></i>
          </li>
        </ul>
      </div>
    </div>

    <hr />

    <!-- Gửi ngay / Lên lịch gửi: chỉ áp dụng khi soạn theo template -->
    <div v-if="composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE" class="row">
      <div class="col-md-12">
        <BasicRadio
          label="Thời điểm gửi"
          name="sendTiming"
          :options="sendTimingOptions"
          :value.sync="sendTiming"
          :inline="true"
        />
      </div>
    </div>
    <div v-if="composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE && sendTiming === BCB_SEND_TIMING.SCHEDULED" class="row mb-3">
      <div class="col-md-6">
        <BasicDatePicker
          label="Ngày giờ gửi"
          placeholder="Chọn ngày giờ muốn gửi"
          name="scheduledTimeValue"
          type="datetime"
          format="YYYY-MM-DD HH:mm"
          value-type="format"
          :value.sync="scheduledTimeValue"
        />
        <small class="text-muted d-block mt-1">
          Email sẽ được hệ thống tự động gửi vào đúng thời điểm này, không cần giữ trình duyệt mở chờ.
        </small>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-4">
      <LoadingButton class="btn btn-success" :class="{ disabled: !canSubmit }" :handler="confirmSend">
        <i class="fas fa-paper-plane mr-2"></i>{{ isScheduledSend ? 'Lên lịch gửi' : 'Gửi thông báo' }}
      </LoadingButton>
    </div>
  </div>
</template>

<script>
import {
  CUSTOMER_STATUS,
  CUSTOMER_STATUS_WORKFLOW_OPTIONS,
  BCB_ADHOC_EMAIL_MODE,
  BCB_ADHOC_EMAIL_MODE_OPTIONS,
  BCB_SEND_TIMING,
} from '../constants/index.js';

const RECIPIENT_MODE = { STATUS: 0, INDIVIDUAL: 1 };

export default {
  name: 'BcbCampaignSendNotification',
  props: {
    campaignId: { type: [String, Number], required: true },
  },
  data() {
    return {
      BCB_ADHOC_EMAIL_MODE,
      BCB_SEND_TIMING,
      RECIPIENT_MODE,
      // BasicRadio expects options in { value, name } format
      composeModeOptions: BCB_ADHOC_EMAIL_MODE_OPTIONS.map((o) => ({ value: o.value, name: o.text })),
      recipientModeOptions: [
        { value: RECIPIENT_MODE.STATUS, name: 'Gửi theo trạng thái' },
        { value: RECIPIENT_MODE.INDIVIDUAL, name: 'Gửi cá nhân (tìm kiếm)' },
      ],
      sendTimingOptions: [
        { value: BCB_SEND_TIMING.NOW, name: 'Gửi ngay' },
        { value: BCB_SEND_TIMING.SCHEDULED, name: 'Lên lịch gửi' },
      ],
      composeMode: BCB_ADHOC_EMAIL_MODE.TEMPLATE,
      sendTiming: BCB_SEND_TIMING.NOW,
      scheduledTimeValue: '',

      templateOptions: [],
      selectedTemplate: null,

      customSubject: '',
      customBody: '',
      attachments: [],

      recipientMode: RECIPIENT_MODE.STATUS,
      statusFilterOptions: CUSTOMER_STATUS_WORKFLOW_OPTIONS,
      statusSelections: [],

      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      searched: false,

      selectedRecipientsMap: {},

      initialized: false,
    };
  },
  watch: {
    composeMode(newVal) {
      if (newVal === BCB_ADHOC_EMAIL_MODE.TEMPLATE) {
        // Chuyển sang dùng template có sẵn -> xoá field của soạn tuỳ chỉnh
        this.customSubject = '';
        this.customBody = '';
        this.attachments = [];
      } else {
        // Chuyển sang soạn tuỳ chỉnh -> xoá field của dùng template (kể cả thời điểm gửi, vì custom luôn gửi liền)
        this.selectedTemplate = null;
        this.sendTiming = BCB_SEND_TIMING.NOW;
        this.scheduledTimeValue = '';
      }
      // Đổi chế độ soạn -> xoá luôn lựa chọn người nhận đã chọn trước đó (trạng thái + cá nhân + kết quả tìm kiếm)
      this.statusSelections = [];
      this.selectedRecipientsMap = {};
      this.searchKeyword = '';
      this.searchResults = [];
      this.searched = false;
    },
    sendTiming(newVal) {
      if (newVal === BCB_SEND_TIMING.NOW) {
        // Chuyển sang gửi ngay -> xoá field của lên lịch gửi
        this.scheduledTimeValue = '';
      }
    },
    recipientMode(newVal) {
      if (newVal === RECIPIENT_MODE.INDIVIDUAL) {
        // Chuyển sang gửi cá nhân -> xoá lựa chọn trạng thái
        this.statusSelections = [];
      } else {
        // Chuyển sang gửi theo trạng thái -> xoá lựa chọn cá nhân + kết quả tìm kiếm
        this.selectedRecipientsMap = {};
        this.searchKeyword = '';
        this.searchResults = [];
        this.searched = false;
      }
    },
  },
  computed: {
    selectedRecipientIds() {
      return Object.keys(this.selectedRecipientsMap);
    },
    selectedRecipientsList() {
      return Object.values(this.selectedRecipientsMap);
    },
    // BasicSelect có thể trả object (change-value-by-object) hoặc id thô tuỳ cấu hình,
    // resolve an toàn ở đây để không bao giờ gửi 'undefined' lên backend.
    selectedTemplateId() {
      if (!this.selectedTemplate) return null;
      return typeof this.selectedTemplate === 'object' ? this.selectedTemplate.id : this.selectedTemplate;
    },
    selectedTemplatePreview() {
      if (!this.selectedTemplateId) return null;
      if (typeof this.selectedTemplate === 'object' && this.selectedTemplate.messageContain) {
        return this.selectedTemplate.messageContain;
      }
      const found = this.templateOptions.find((t) => t.id === this.selectedTemplateId);
      return found?.messageContain || null;
    },
    isScheduledSend() {
      return this.composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE && this.sendTiming === BCB_SEND_TIMING.SCHEDULED;
    },
    canSubmit() {
      const hasRecipientBasis =
        this.recipientMode === RECIPIENT_MODE.STATUS
          ? this.statusSelections.length > 0
          : this.selectedRecipientIds.length > 0;
      if (!hasRecipientBasis) return false;
      if (this.isScheduledSend && !this.isScheduledTimeValid) return false;
      if (this.composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE) return !!this.selectedTemplateId;
      return !!this.customSubject;
    },
    isScheduledTimeValid() {
      if (!this.scheduledTimeValue) return false;
      return this.$moment(this.scheduledTimeValue, 'YYYY-MM-DD HH:mm').isAfter(this.$moment());
    },
    recipientColumns() {
      return [
        { key: 'fullName', label: 'Họ và tên' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Số điện thoại' },
        { key: 'status', label: 'Trạng thái' },
      ];
    },
  },
  methods: {
    async initialize() {
      if (this.initialized) return;
      this.initialized = true;
      await this.loadTemplates();
    },
    async loadTemplates() {
      try {
        const res = await this.$api.get('MessageTemplates', { params: { page: 1, size: 1000 } });
        const raw = res?.data ?? res;
        this.templateOptions = raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tải danh sách template.' });
      }
    },
    async fetchCustomers(params) {
      const res = await this.$api.get('BcbCampaignCustomer', {
        params: { campaignId: this.campaignId, page: 1, size: 1000, ...params },
      });
      const raw = res?.data ?? res;
      return raw?.items ?? raw?.data?.items ?? (Array.isArray(raw) ? raw : []);
    },
    async searchCustomers() {
      const keyword = this.searchKeyword.trim();
      if (!keyword) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng nhập tên hoặc số điện thoại để tìm kiếm.' });
        return;
      }
      this.searchLoading = true;
      this.searched = true;
      try {
        // Backend chỉ hỗ trợ lọc riêng theo fullName hoặc phone (AND), nên tìm cả 2 rồi gộp kết quả không trùng lặp.
        const [byName, byPhone] = await Promise.all([
          this.fetchCustomers({ fullName: keyword }),
          this.fetchCustomers({ phone: keyword }),
        ]);
        const merged = new Map();
        [...byName, ...byPhone].forEach((item) => merged.set(item.id, item));
        this.searchResults = Array.from(merged.values());
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể tìm kiếm khách hàng.' });
      } finally {
        this.searchLoading = false;
      }
    },
    onSelectSearchResults(selectedRows) {
      // Bỏ chọn toàn bộ KH thuộc kết quả tìm kiếm hiện tại, rồi thêm lại KH vừa được tick
      // để không mất lựa chọn của các lượt tìm kiếm trước đó.
      this.searchResults.forEach((item) => {
        this.$delete(this.selectedRecipientsMap, item.id);
      });
      selectedRows.forEach((item) => {
        this.$set(this.selectedRecipientsMap, item.id, item);
      });
    },
    removeRecipient(id) {
      this.$delete(this.selectedRecipientsMap, id);
    },
    onFilesSelected(e) {
      const MAX_TOTAL_SIZE = 100 * 1024 * 1024;
      const files = Array.from(e.target.files || []);
      const currentTotal = this.attachments.reduce((sum, f) => sum + f.size, 0);
      const newTotal = files.reduce((sum, f) => sum + f.size, currentTotal);
      if (newTotal > MAX_TOTAL_SIZE) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Tổng dung lượng file đính kèm không được vượt quá 100MB.' });
        e.target.value = '';
        return;
      }
      this.attachments.push(...files);
      e.target.value = '';
    },
    removeAttachment(idx) {
      this.attachments.splice(idx, 1);
    },
    formatSize(bytes) {
      if (!bytes) return '0 KB';
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(0)} KB`;
      return `${(kb / 1024).toFixed(1)} MB`;
    },
    getCustomerStatusLabel(status) {
      return CUSTOMER_STATUS[status]?.label || '—';
    },
    getCustomerStatusStyle(status) {
      const cfg = CUSTOMER_STATUS[status];
      return {
        backgroundColor: cfg?.bg || '#F1EFE8',
        color: cfg?.color || '#444441',
      };
    },
    async confirmSend() {
      if (!this.canSubmit) return;
      if (this.composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE && !this.selectedTemplateId) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng chọn template trước khi gửi.' });
        return;
      }
      if (this.isScheduledSend && !this.isScheduledTimeValid) {
        this.$toastr.e({ title: 'Lỗi', msg: 'Vui lòng chọn ngày giờ gửi ở tương lai.' });
        return;
      }

      const isByStatus = this.recipientMode === RECIPIENT_MODE.STATUS;
      const recipientDesc = isByStatus
        ? `toàn bộ khách hàng thuộc ${this.statusSelections.length} trạng thái đã chọn`
        : `${this.selectedRecipientIds.length} khách hàng đã chọn`;
      const confirmMsg = this.isScheduledSend
        ? `Bạn có chắc muốn lên lịch gửi email lúc ${this.scheduledTimeValue} cho ${recipientDesc} không?`
        : `Bạn có chắc muốn gửi email cho ${recipientDesc} không?`;

      const confirmed = await this.$bvModal.msgBoxConfirm(confirmMsg, {
        title: 'Xác nhận gửi thông báo',
        okTitle: 'Gửi',
        cancelTitle: 'Hủy',
        okVariant: 'success',
        centered: true,
      });
      if (!confirmed) return;
      await this.sendAdhocEmail();
    },
    buildRecipientFormData(formData) {
      formData.append('campaignId', this.campaignId);
      formData.append('recipientMode', this.recipientMode);
      if (this.recipientMode === RECIPIENT_MODE.STATUS) {
        this.statusSelections.forEach((status) => formData.append('statuses', status));
      } else {
        this.selectedRecipientIds.forEach((id) => formData.append('campaignCustomerIds', id));
      }
    },
    async sendAdhocEmail() {
      try {
        const formData = new FormData();
        this.buildRecipientFormData(formData);

        let endpoint;
        if (this.composeMode === BCB_ADHOC_EMAIL_MODE.TEMPLATE) {
          endpoint = 'BcbCampaignNotification/SendAdhocTemplateEmail';
          formData.append('templateMessageId', this.selectedTemplateId);
          formData.append('sendTiming', this.sendTiming);
          if (this.sendTiming === BCB_SEND_TIMING.SCHEDULED) {
            formData.append('scheduledTime', this.convertDateToTimestamp(this.scheduledTimeValue, 'YYYY-MM-DD HH:mm') + 7 * 3600);
          }
        } else {
          endpoint = 'BcbCampaignNotification/SendAdhocCustomEmail';
          formData.append('customSubject', this.customSubject);
          formData.append('customBody', this.customBody || '');
          this.attachments.forEach((file) => formData.append('attachments', file));
        }

        await this.$api.post(endpoint, formData);
        this.$toastr.s({
          title: 'Thành công',
          msg: this.isScheduledSend
            ? `Đã lên lịch gửi lúc ${this.scheduledTimeValue}, hệ thống sẽ tự động gửi khi tới giờ.`
            : 'Đã gửi thông báo.',
        });
        this.selectedRecipientsMap = {};
        this.attachments = [];
      } catch (e) {
        this.$toastr.e({ title: 'Lỗi', msg: e?.message || 'Không thể gửi thông báo.' });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
.template-preview {
  background-color: #f8f9fa;
  max-height: 300px;
  overflow-y: auto;
}
.attachment-list {
  li {
    font-size: 0.9rem;
  }
}
.cursor-pointer {
  cursor: pointer;
}
</style>
